// CastingDirectorOptions.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CoHostPersonality } from '../../../types/studio.types';
import { invokeCastingDirector } from '../api-studio';
import AIHeader from 'components/common/AIHeader';

interface CastingDirectorOptionsProps {
  host: CoHostPersonality;
  websitePK: string;
  onCastingDirectorComplete: (voiceId: string, explanation: string, blacklistedVoiceIds: string[]) => void;
  disabled?: boolean;
}

const CastingDirectorOptions: React.FC<CastingDirectorOptionsProps> = ({
  host,
  websitePK,
  onCastingDirectorComplete,
  disabled = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [blacklistCurrentVoice, setBlacklistCurrentVoice] = useState(false);
  const [localBlacklist, setLocalBlacklist] = useState<string[]>([]);

  // Initialize from host on first mount
  useEffect(() => {
    setLocalBlacklist(host.blacklistedVoiceIds || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only reset local blacklist when switching to a different cohost identity (not when voiceId updates)
  const hostIdentityRef = useRef(`${host.name}|${host.nickname}`);
  useEffect(() => {
    const newIdentity = `${host.name}|${host.nickname}`;
    if (hostIdentityRef.current !== newIdentity) {
      setLocalBlacklist(host.blacklistedVoiceIds || []);
      hostIdentityRef.current = newIdentity;
    }
  }, [host.name, host.nickname, host.blacklistedVoiceIds]);

  // Handle copying the voiceId to clipboard
  const handleCopyVoiceId = () => {
    if (host.voiceId) {
      navigator.clipboard.writeText(host.voiceId);
      setCopySnackbarOpen(true);
    }
  };

  // Close the copy notification
  const handleCloseSnackbar = () => {
    setCopySnackbarOpen(false);
  };

  // Handle invoking the casting director
  const handleCastingDirectorClick = async () => {
    try {
      // Build an accumulated blacklist so the server sees all prior entries
      let updatedHost = { ...host } as CoHostPersonality;
      const baseBlacklist = Array.isArray(localBlacklist) ? localBlacklist : [];
      // ensure uniqueness
      const set = new Set(baseBlacklist);
      if (blacklistCurrentVoice && host.voiceId) {
        set.add(host.voiceId);
      }
      const nextBlacklist = Array.from(set);
      updatedHost.blacklistedVoiceIds = nextBlacklist;

      const response = await invokeCastingDirector(websitePK, updatedHost);
      onCastingDirectorComplete(response.voiceId || '', response.explanation || '', nextBlacklist);
      // Persist accumulated blacklist locally for subsequent runs
      setLocalBlacklist(updatedHost.blacklistedVoiceIds || []);
      setIsGenerating(false);
      setBlacklistCurrentVoice(false); // Reset checkbox
    } catch (error) {
      console.error('Error invoking casting director:', error);
      setIsGenerating(false);
      setBlacklistCurrentVoice(false); // Reset checkbox
    }
  };

  // Custom dialog content with blacklist checkbox
  const customDialogContent = host.voiceId ? (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={blacklistCurrentVoice}
            onChange={(_, checked) => setBlacklistCurrentVoice(checked)}
            color="primary"
          />
        }
        label="Add the current voiceID to the list not to use for this cohost in the future."
      />
    </Box>
  ) : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          fullWidth
          label="Voice ID"
          value={host.voiceId || 'No voice assigned yet'}
          InputProps={{
            readOnly: true,
            endAdornment: host.voiceId && (
              <Tooltip title="Copy Voice ID">
                <IconButton
                  edge="end"
                  onClick={handleCopyVoiceId}
                  disabled={disabled || !host.voiceId}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
          disabled={disabled}
          variant="outlined"
          size="small"
        />
        
        <Box sx={{ ml: 2 }}>
          <AIHeader
            tooltip="Find best matching voice model with AI"
            dialogTitle="Find best matching voice model with AI"
            dialogDescription="This operation will use AI to find the best matching voice profile based on this host information. The process may take several minutes to complete. You can wait or return later to see the results."
            customDialogContent={customDialogContent}
            progressMessage="AI is finding the best matching voice profile based on this host information. This will take a few minutes..."
            onGenerateConfirm={handleCastingDirectorClick}
            isGenerating={isGenerating}
            setIsGenerating={setIsGenerating}
            generateButtonText="Generate"
            cancelButtonText="Cancel"
            disabled={disabled}
          />
        </Box>
      </Box>

      {host.castingDirectorExplanation && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Casting Director Explanation
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={host.castingDirectorExplanation}
            InputProps={{ readOnly: true }}
            disabled={disabled}
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Voice ID copied to clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CastingDirectorOptions;